/**
 * Jovii — client-side routing.
 *
 * A hundred lines instead of a router dependency, because the whole app is six
 * screens. Ordinary `<a href="/movie/tt0133093">` links work: one delegated click
 * listener intercepts same-origin navigations and leaves modifier-clicks,
 * middle-clicks, downloads and external links to the browser.
 *
 * URLs are the app's vocabulary and are meant to be shared:
 *   /                          home
 *   /search?q=matrix           search
 *   /movies  /shows            browse one kind: popular → new → by genre
 *   /movies/Comedy             one genre of one kind
 *   /collection/heists         a curated collection (see lib/ui/collections.ts)
 *   /person/Q23547             a person — actor or director (see lib/ui/person.ts)
 *   /person/Bong%20Joon-ho     the same page keyed by name, resolved on arrival
 *   /categories                every genre, both kinds
 *   /my                        My Jovii — watching now, saved, suggested
 *   /movie/tt0133093           a film
 *   /series/tt0903747          a series (last-watched season)
 *   /series/tt0903747/2        a season
 *   /series/tt0903747/2/5      a season, with one episode highlighted
 *   /watch/movie/tt0133093     playing a film
 *   /watch/series/tt0903747/2/5
 *   /room/tulip-421            join a group; adopt whatever it is watching
 *   /profile  /friends  /settings
 *
 * SINGULAR IS A TITLE, PLURAL IS A SHELF. `/movie/tt…` is one film; `/movies` is
 * the browse screen. They cannot collide because a title path always carries an
 * IMDb id, and a browse path never does.
 */

import type { MediaKind } from '$types';

export type Route =
  | { name: 'home' }
  | { name: 'welcome' }
  | { name: 'signin' }
  | { name: 'search'; q: string }
  /** One kind's shelf. `genre: null` is the whole shelf: popular, then new, then by genre. */
  | { name: 'browse'; kind: MediaKind; genre: string | null }
  /** A curated collection. The slug may name nothing; the screen says so. */
  | { name: 'collection'; slug: string }
  /**
   * One person. The key is a Wikidata QID when the link's builder had one
   * (cast faces carry `member.qid`) or a display name otherwise (director
   * credits); the page resolves either and fails soft to name-only.
   */
  | { name: 'person'; key: string }
  | { name: 'categories' }
  | { name: 'mine' }
  | { name: 'profile' }
  | { name: 'friends' }
  | { name: 'title'; kind: MediaKind; imdbId: string; season: number | null; episode: number | null }
  | {
      name: 'watch';
      kind: MediaKind;
      imdbId: string;
      season: number | null;
      episode: number | null;
      roomId: string | null;
      /** Force the link picker open on arrival (used by "Change source"). */
      pick: boolean;
    }
  | { name: 'room'; roomId: string }
  | { name: 'settings' }
  | { name: 'notfound'; path: string };

const IMDB = /^tt\d{5,10}$/;

function num(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/**
 * A genre out of a path segment. Cinemeta's vocabulary is `Sci-Fi`, `Reality-TV`,
 * `Film-Noir` — letters and hyphens only — so anything else is not a genre and the
 * shelf falls back to its whole self rather than asking the catalogue a question
 * it will answer with an empty list.
 */
function genreOf(raw: string | undefined): string | null {
  if (raw === undefined) return null;
  try {
    const decoded = decodeURIComponent(raw);
    return /^[A-Za-z][A-Za-z-]{1,24}$/.test(decoded) ? decoded : null;
  } catch {
    return null;
  }
}

export function parse(url: URL): Route {
  const segments = url.pathname.split('/').filter(Boolean);
  const q = url.searchParams;

  if (segments.length === 0) return { name: 'home' };

  switch (segments[0]) {
    case 'welcome': return {name:'welcome'};
    case 'signin': return {name:'signin'};
    case 'search':
      return { name: 'search', q: q.get('q') ?? '' };

    case 'settings':
      return { name: 'settings' };

    case 'categories':
      return { name: 'categories' };

    case 'my':
      return { name: 'mine' };

    case 'profile':
      return { name: 'profile' };

    case 'friends':
      return { name: 'friends' };


    case 'movies':
      return { name: 'browse', kind: 'movie', genre: genreOf(segments[1]) };

    case 'shows':
      return { name: 'browse', kind: 'series', genre: genreOf(segments[1]) };

    case 'collection':
      // Slugs are lowercase-and-hyphens by construction (fetch-collections.mjs).
      if (segments[1] && /^[a-z0-9][a-z0-9-]{0,39}$/.test(segments[1])) {
        return { name: 'collection', slug: segments[1] };
      }
      break;

    case 'person': {
      // A QID (`Q23547`) or a percent-encoded display name. Names are capped
      // at 80 characters — longer is not a person, it is a mistake — and a
      // malformed encoding falls through to notfound rather than throwing.
      if (segments[1]) {
        try {
          const key = decodeURIComponent(segments[1]).trim();
          if (key && key.length <= 80) return { name: 'person', key };
        } catch {
          /* malformed %-sequence: not a person link */
        }
      }
      break;
    }

    case 'movie':
      if (segments[1] && IMDB.test(segments[1])) {
        return { name: 'title', kind: 'movie', imdbId: segments[1], season: null, episode: null };
      }
      break;

    case 'series':
      if (segments[1] && IMDB.test(segments[1])) {
        return {
          name: 'title',
          kind: 'series',
          imdbId: segments[1],
          season: num(segments[2]),
          episode: num(segments[3])
        };
      }
      break;

    case 'watch': {
      const kind = segments[1];
      const id = segments[2];
      if ((kind === 'movie' || kind === 'series') && id && IMDB.test(id)) {
        return {
          name: 'watch',
          kind,
          imdbId: id,
          season: kind === 'series' ? (num(segments[3]) ?? 1) : null,
          episode: kind === 'series' ? (num(segments[4]) ?? 1) : null,
          roomId: q.get('room'),
          pick: q.get('pick') === '1'
        };
      }
      break;
    }

    case 'room':
      if (segments[1]) return { name: 'room', roomId: segments[1] };
      break;
  }

  return { name: 'notfound', path: url.pathname };
}

/* ── URL builders. Every link in the app comes from one of these. ─────────── */

/** The browse shelf for one kind, optionally narrowed to one genre. */
export function browseHref(kind: MediaKind, genre?: string | null): string {
  const base = kind === 'series' ? '/shows' : '/movies';
  return genre ? `${base}/${encodeURIComponent(genre)}` : base;
}

/** A curated collection's page. */
export function collectionHref(slug: string): string {
  return `/collection/${slug}`;
}

/**
 * A person's page. Prefer the QID — it is unambiguous and saves the page a
 * name resolution; a bare name is legal and resolves on arrival (person.ts).
 */
export function personHref(person: { qid?: string | undefined; name: string }): string {
  return `/person/${person.qid ?? encodeURIComponent(person.name)}`;
}

export function titleHref(kind: MediaKind, imdbId: string, season?: number | null, episode?: number | null): string {
  if (kind === 'movie') return `/movie/${imdbId}`;
  let href = `/series/${imdbId}`;
  if (season !== null && season !== undefined) href += `/${season}`;
  if (episode !== null && episode !== undefined) href += `/${episode}`;
  return href;
}

/* ── The group this tab is in ─────────────────────────────────────────────── */

/**
 * BEING IN A GROUP IS A MODE, NOT A URL.
 *
 * Room-first was the half of the headline requirement that did not work: from
 * `/room/tulip-421` every way out dropped the room. "Find something" went to `/`,
 * pressing Play went to a `/watch/…` with no `?room=`, and the friend waiting on
 * the invite link waited for ever — while the screen they were on promised
 * "anyone opening it lands in the same room".
 *
 * Every play link in the app is built by `watchHref`, so this is the one place
 * that has to know. Pass `roomId` explicitly to override it (a brand-new group);
 * pass `null` to deliberately watch alone.
 *
 * Session-scoped, like the sync engine's own resume token: a reload keeps the
 * group, a second tab is a second person.
 */
const ROOM_KEY = 'jove.together.room';

function storedRoom(): string | null {
  try {
    return sessionStorage.getItem(ROOM_KEY);
  } catch {
    return null; // Safari private mode  // IOS-UNVERIFIED
  }
}

const group: { roomId: string | null } = $state({
  roomId: typeof sessionStorage === 'undefined' ? null : storedRoom()
});

/** The group this tab is in, or null when watching alone. */
export function currentRoom(): string | null {
  return group.roomId;
}

/**
 * THE ROOM THIS TAB OPENED ITSELF — as opposed to one it walked into.
 *
 * Under auto-invite every playback opens a room, and a room dies the moment its
 * LAST player closes (`ownerHolds` in internal/rooms/hub.ts — held by anybody,
 * dead when empty). A solo watcher is their room's only member, so between films
 * the sticky code in `currentRoom` is usually a tombstone — and joining a
 * tombstone is an ejection to the title page for no reason the viewer can see.
 * The watch screen therefore snapshot-checks a carried code before trusting it,
 * but ONLY for rooms this tab opened: a room somebody else let you into should
 * say "that room has ended" when it has, not silently become a different room.
 * This marker is what tells those two cases apart.
 *
 * Session-scoped like ROOM_KEY, and one id only: a tab owns at most one live room.
 */
const MINE_KEY = 'jove.together.room.mine';
// A document navigation disconnects its owned room. Do not carry that dying
// room into the next document and race its asynchronous server closure.
if (typeof window !== 'undefined') window.addEventListener('pagehide', () => {
  if (group.roomId && roomIsMine(group.roomId)) setCurrentRoom(null);
});

export function markRoomMine(roomId: string): void {
  try {
    sessionStorage.setItem(MINE_KEY, roomId);
  } catch {
    /* private mode: the worst case is one bounced join, exactly as before */
  }
}

export function roomIsMine(roomId: string): boolean {
  try {
    return sessionStorage.getItem(MINE_KEY) === roomId;
  } catch {
    return false;
  }
}

/** Called by whichever screen learns which group we are in — or that we left one. */
export function setCurrentRoom(roomId: string | null): void {
  if (group.roomId === roomId) return;
  group.roomId = roomId;
  try {
    if (roomId === null) sessionStorage.removeItem(ROOM_KEY);
    else sessionStorage.setItem(ROOM_KEY, roomId);
  } catch {
    /* private mode: the group still holds for this screen */
  }
}

/**
 * WHY THE GROUP WENT, WHEN NOBODY HERE ASKED IT TO.
 *
 * A room ends when the person who started it leaves, so a guest is taken out of the
 * player mid-film having done nothing at all (`web/src/lib/sync/reducer.ts`, the
 * `no-room` branch). The screen that learns this is the one being unmounted, so it
 * cannot be the one that explains it — an ejection with no sentence attached is
 * indistinguishable from a crash, and that is the complaint this whole pass is about
 * arriving from the other side.
 *
 * So the sentence outlives the screen by exactly one navigation: the watch screen
 * records it on its way out, the title page it lands on draws it, and reading it is
 * what clears it. Deliberately NOT in the URL — a reload an hour later must not
 * announce a room that ended this evening — and deliberately not a toast queue, which
 * is a system for a problem this app does not have.
 */
const ending: { roomId: string | null } = $state({ roomId: null });

/** The room that ended under us, if the next screen has not yet said so. */
export function endedRoom(): string | null {
  return ending.roomId;
}

/** Record the ending, and leave the group. One call, because they are one fact. */
export function noteRoomEnded(roomId: string): void {
  ending.roomId = roomId;
  setCurrentRoom(null);
}

export function clearRoomEnded(): void {
  ending.roomId = null;
}

export function watchHref(opts: {
  kind: MediaKind;
  imdbId: string;
  season?: number | null;
  episode?: number | null;
  /** Omit to stay in whatever group this tab is in; `null` to watch alone. */
  roomId?: string | null;
  pick?: boolean;
}): string {
  let href = `/watch/${opts.kind}/${opts.imdbId}`;
  if (opts.kind === 'series') href += `/${opts.season ?? 1}/${opts.episode ?? 1}`;
  const params = new URLSearchParams();
  const roomId = opts.roomId === undefined ? group.roomId : opts.roomId;
  if (roomId) params.set('room', roomId);
  if (opts.pick) params.set('pick', '1');
  const query = params.toString();
  return query ? `${href}?${query}` : href;
}

export function searchHref(q: string): string {
  return q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search';
}

/* ── Live route ──────────────────────────────────────────────────────────── */

function historyDepth(): number {
  const value = typeof history === 'undefined' ? undefined : history.state?.jt;
  return Number.isSafeInteger(value) && value >= 0 ? value : 0;
}
let nextEntry = 0;
function entryId(): string { return `${Date.now()}:${++nextEntry}`; }

const initial = typeof location !== 'undefined' ? new URL(location.href) : new URL('http://localhost/');

// Svelte teardown intentionally reads the previous reactive snapshot. Lifecycle
// ownership needs the navigation already committed by the router, not that snapshot.
let committedRoute: Route = parse(initial);
export function currentRoute(): Route { return committedRoute; }

export const nav: { route: Route; url: URL; depth: number } = $state({
  route: parse(initial),
  url: initial,
  /** How many pushes we have made. 0 means Back leaves the app. */
  depth: historyDepth()
});

/** Vertical scroll of each history entry, so Back returns to where you were. */
const scrollMemory = new Map<string, number>();

/**
 * WHICH HISTORY ENTRY THIS IS — the depth stamp plus the address.
 *
 * The depth is what separates two visits to the same URL: opening a film from
 * Home and then opening the same film again from a rail two screens later are
 * different entries and must remember different things. Exported because
 * `dpad.ts` remembers something else about each entry — which tile the remote
 * was on — and two maps keyed by two subtly different strings is the kind of bug
 * that only shows up on the fourth press of Back.
 */
export function historyKey(): string {
  return `${history.state?.jtKey ?? historyDepth()}:${location.pathname}${location.search}`;
}

function apply(): void {
  const url = new URL(location.href);
  committedRoute = parse(url);
  nav.depth = historyDepth();
  nav.url = url;
  nav.route = committedRoute;
}

/** Navigate. `replace` swaps the current entry instead of adding one. */
export function go(href: string, options: { replace?: boolean; keepScroll?: boolean } = {}): void {
  const target = new URL(href, location.href);
  if (target.href === location.href && !options.replace) return;

  if (!options.keepScroll) scrollMemory.set(historyKey(), window.scrollY);

  const state = { ...history.state, jt: options.replace ? historyDepth() : historyDepth() + 1,
    jtKey: options.replace ? history.state?.jtKey ?? entryId() : entryId() };
  if (options.replace) {
    history.replaceState(state, '', target.href);
  } else {
    history.pushState(state, '', target.href);

  }
  apply();

  if (!options.keepScroll) {
    // A new screen starts at the top; scrollMemory only serves Back.
    window.scrollTo(0, 0);
  }
}

/** Back, or home when this is the first page in the tab (a shared link). */
export function back(fallback = '/'): void {
  if (historyDepth() > 0) history.back();
  else go(fallback, { replace: true });
}

let started = false;

/** Called once from App.svelte. Wires the delegated link handler and popstate. */
export function startRouter(): () => void {
  if (started) return () => {};
  started = true;

  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  history.replaceState({ ...history.state, jt: historyDepth(), jtKey: history.state?.jtKey ?? entryId() }, '', location.href);

  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = (event.target as Element | null)?.closest?.('a');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (anchor.target && anchor.target !== '_self') return;
    if (anchor.hasAttribute('download') || anchor.dataset.native !== undefined) return;

    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#') || /^[a-z]+:/i.test(href)) return;

    const target = new URL(href, location.href);
    if (target.origin !== location.origin) return;

    event.preventDefault();
    go(target.pathname + target.search, { replace: anchor.dataset.replace !== undefined });
  };

  const onPop = () => {
    apply();
    const y = scrollMemory.get(historyKey()) ?? 0;
    // Let the new screen render before restoring, or we scroll an empty page.
    requestAnimationFrame(() => window.scrollTo(0, y));
  };

  document.addEventListener('click', onClick);
  window.addEventListener('popstate', onPop);
  apply();

  return () => {
    document.removeEventListener('click', onClick);
    window.removeEventListener('popstate', onPop);
    started = false;
  };
}
